import {HiristAppIconNew} from '@jobseeker/lotus/assets/images/resources/new-logo';
import Button from '@jobseeker/lotus/elements/Button';
import {CrossHirist} from '@jobseeker/lotus/assets/icons';
import {Box, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Image} from '@jobseeker/lotus/components/Image';
import {shallowEqual, useSelector} from 'react-redux';
import type {AppState} from '@jobseeker/common-core/types';
import {getBannerInfo} from '@jobseeker/feature-ads/utils';
import {useEffect, useState} from 'react';
import {setCookie} from 'cookies-next';
import {
  getUbaPageName,
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';

const Banner = styled('div')(({theme}) => ({
  width: '100%',
  backgroundColor: 'white',
  color: theme.palette.primary.contrastText,
  paddingLeft: theme.spacing(1.5),
  paddingTop: theme.spacing(1),
  paddingRight: theme.spacing(1.5),
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(1),
  textAlign: 'center',
  position: 'fixed',
  top: 0,
  zIndex: 1100,
  borderBottom: '1px solid #e6e6e6',
}));

interface BannerType {
  heading: string;
  subHeading: string;
  redirectLink: string;
  imageUrl: string;
  button: string;
}

type HiristPromotionsProps = {
  onLinkageCrossClick: () => void;
};

const HiristPromotions: React.FC<HiristPromotionsProps> = ({
  onLinkageCrossClick,
}) => {
  const [bannerData, setBannerData] = useState<BannerType | null>(null);
  const page = useSelector((state: AppState) => state.app.page, shallowEqual);
  const interceptor: any = useInterceptor();
  const {
    ads: {hiristDownloadBanner},
    gaEvents: {
      downloadApp: {topBanner},
    },
  } = interceptor;

  const slots = {
    TOP: hiristDownloadBanner?.[0]?.slot,
    MIDDLE: hiristDownloadBanner?.[1]?.slot,
    BOTTOM: hiristDownloadBanner?.[2]?.slot,
  };

  const isJobDetailPage = page === 'jobdetail';
  const startTime = new Date().getDate() % 3;
  const bannerId = page === 'jobdetail' ? startTime + 1 : '';

  const handleUbaTracking = (action = 'view', typeClick = 'CTA') => {
    const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
    if (isAppIimjobs) return;
    const trackingData = {
      pageName: getUbaPageName(),
      linkageName: 'Top Banner Download App',
      linkageType: 'topBanner',
      sessionUsername: sessionStorage.getItem('userId') || '',
      ...(action === 'click' && {typeClick: typeClick}),
    };
    TrackEventUBA(
      action === 'click' ? 'appDownloadLinkageClick' : 'appDownloadLinkageView',
      action,
      trackingData
    );
  };

  useEffect(() => {
    const fetchBannerData = async () => {
      const bannerObj = await getBannerInfo(page, slots);
      if (bannerObj?.imageUrl?.endsWith('/1703253005645.webp')) {
        // TODO: Remove this condition once the banner image is updated from the backend.
        // This is added to handle the scenario when the backend is sending an outdated image url.
        bannerObj.imageUrl = HiristAppIconNew;
      }
      setBannerData(bannerObj);
    };
    fetchBannerData();
  }, [page]);

  useEffect(() => {
    handleTracking(topBanner['view'], {
      event_category: topBanner['eventCategory'],
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Text: `Banner ${bannerId}`,
    });
    handleUbaTracking('view');
  }, []);

  const redirectToApp = (redirectLink: string) => {
    setTimeout(() => {
      handleTracking(topBanner['click'], {
        event_category: topBanner['eventCategory'],
        Origin: page,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        Text: `Banner ${bannerId}`,
      });
      openUrl(redirectLink);
      handleUbaTracking('click', 'CTA');
    }, 0);
  };

  const handleCloseIcon = () => {
    setTimeout(() => {
      handleTracking(topBanner['close'], {
        event_category: topBanner['eventCategory'],
        Origin: page,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Device_Type: sessionStorage.getItem('deviceType') || 'web',
        Status: sessionStorage.getItem('status') || 'LoggedOut',
        Text: `Banner ${bannerId}`,
      });
      handleUbaTracking('click', 'Close');
      onLinkageCrossClick && onLinkageCrossClick();
      const expiryDate = new Date();
      expiryDate.setTime(expiryDate.getTime() + 24 * 60 * 60 * 1000);
      setCookie('linkage_banner', '1', {path: '/', expires: expiryDate});
    }, 0);
  };

  return bannerData ? (
    <Banner
      sx={{
        bgcolor: isJobDetailPage ? '#fff3ef' : 'white',
        height: isJobDetailPage ? '96px' : '80px',
      }}
    >
      <Box
        display={'flex'}
        justifyContent={'flex-start'}
        alignItems={'center'}
        gap={1.25}
      >
        <Box
          display={'flex'}
          justifyContent={isJobDetailPage ? 'space-between' : 'flex-start'}
          alignItems={isJobDetailPage ? 'flex-start' : 'center'}
          gap={1.25}
          width={'100%'}
        >
          {!isJobDetailPage && (
            <Box>
              <Image
                src={CrossHirist}
                alt="Promotion"
                width={10}
                height={10}
                onClick={handleCloseIcon}
              />
            </Box>
          )}
          <Box
            display={'flex'}
            alignItems={isJobDetailPage ? 'flex-start' : 'center'}
            gap={1.625}
          >
            <Box
              display={'flex'}
              alignSelf={isJobDetailPage ? 'flex-start' : 'center'}
            >
              <Image
                src={bannerData?.imageUrl || HiristAppIconNew}
                alt="Promotion"
                width={isJobDetailPage ? 40 : 56}
                height={isJobDetailPage ? 42 : 56}
              />
            </Box>
            <Box
              display="flex"
              flexDirection={isJobDetailPage ? 'column' : 'row'}
              alignItems={isJobDetailPage ? 'flex-start' : 'center'}
              justifyContent={'flex-start'}
              gap={1}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
              >
                <Typography
                  variant="subtitle1"
                  textAlign={'left'}
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: isJobDetailPage ? '15px' : '16px',
                  }}
                >
                  {bannerData['heading']}
                </Typography>
                <Typography
                  fontSize={isJobDetailPage ? 13 : 15}
                  sx={{
                    color: 'text.primary',
                    opacity: isJobDetailPage ? 0.6 : 0.8,
                  }}
                  textAlign={'left'}
                  lineHeight={'14px'}
                >
                  {bannerData['subHeading']}
                </Typography>
              </Box>
              <Box>
                {' '}
                <Button
                  sx={{
                    width: {xxs: 'auto', md: 'auto'},
                    fontWeight: isJobDetailPage ? 500 : 800,
                    fontSize: isJobDetailPage ? '14px' : '12px',
                    backgroundColor: 'primary.main',
                    px: isJobDetailPage ? '5px' : '7px',
                    py: '5px',
                    borderRadius: '5px',
                    height: '24px',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                  }}
                  variant="contained"
                  onClick={() => redirectToApp(bannerData['redirectLink'])}
                  target="_blank"
                >
                  {bannerData['button']}
                </Button>
              </Box>
            </Box>
          </Box>
          {isJobDetailPage && (
            <Box>
              <Image
                src={CrossHirist}
                alt="Promotion"
                width={10}
                height={10}
                onClick={handleCloseIcon}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Banner>
  ) : null;
};

export default HiristPromotions;
